/**
 * A8 Reconciliation — Exceptions tab (Accounting Session 1).
 * Pure UI wrapper around already-built endpoints (GET /api/reconciliation/
 * exceptions, POST /api/reconciliation/exceptions/{key}/action) — zero new
 * backend logic. Every action here is one of the 6 the endpoint already
 * supports (confirm/edit/split/reassign/unallocate/reject); which buttons
 * show per row is governed by the same rules services/reconciliation.py
 * enforces (edit needs refType=extraction_line, split/reassign/unallocate
 * need type=unallocated_amount) so a click never hits an avoidable 400.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertTriangle, CheckCircle2, XCircle, Pencil, SplitSquareHorizontal,
  ArrowRightLeft, Undo2, Loader2, History,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXCEPTION_LABELS = {
  arithmetic_mismatch: 'Arithmetic Mismatch',
  unmatched_transaction: 'Unmatched Transaction',
  duplicate_candidate: 'Duplicate Candidate',
  unallocated_amount: 'Unallocated Amount',
  low_confidence_pending_review: 'Low-Confidence Pending Review',
  fy_overlap: 'FY Overlap (Live + Historical)',
};
const TARGET_TYPES = ['ucc', 'traveller', 'trip', 'invoice', 'purchase', 'vendor', 'account_category'];

const ACTION_META = {
  confirm: { label: 'Confirm', icon: CheckCircle2, cls: 'border-emerald-200 text-emerald-700 hover:bg-emerald-50' },
  reject: { label: 'Reject', icon: XCircle, cls: 'border-slate-200 text-slate-600 hover:bg-slate-50' },
  edit: { label: 'Edit', icon: Pencil, cls: 'border-indigo-200 text-indigo-700 hover:bg-indigo-50' },
  split: { label: 'Split', icon: SplitSquareHorizontal, cls: 'border-amber-200 text-amber-700 hover:bg-amber-50' },
  reassign: { label: 'Reassign', icon: ArrowRightLeft, cls: 'border-amber-200 text-amber-700 hover:bg-amber-50' },
  unallocate: { label: 'Unallocate', icon: Undo2, cls: 'border-rose-200 text-rose-700 hover:bg-rose-50' },
};

function availableActions(exc) {
  const actions = ['confirm', 'reject'];
  if (exc.refType === 'extraction_line') actions.push('edit');
  if (exc.type === 'unallocated_amount') actions.push('split', 'reassign', 'unallocate');
  return actions;
}

const ActionDialog = ({ exception, action, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [editField, setEditField] = useState('');
  const [editValue, setEditValue] = useState('');
  const [reassignType, setReassignType] = useState('ucc');
  const [reassignRef, setReassignRef] = useState('');
  const [splitRows, setSplitRows] = useState([{ amount: '', type: 'ucc', referenceId: '' }]);
  const [saving, setSaving] = useState(false);

  const meta = ACTION_META[action];
  const title = {
    confirm: 'Confirm exception', reject: 'Reject exception', edit: 'Edit field',
    split: 'Split unallocated amount', reassign: 'Reassign allocation', unallocate: 'Unallocate',
  }[action];

  const updateSplitRow = (i, patch) => setSplitRows((rows) => rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const submit = async () => {
    const body = { action, reason };
    if (action === 'edit') {
      if (!editField.trim()) { toast.error('Field name is required'); return; }
      body.editField = editField.trim();
      body.editValue = editValue;
    }
    if (action === 'reassign') {
      if (!reassignRef.trim()) { toast.error('Reference ID is required'); return; }
      body.reassignTo = { type: reassignType, referenceId: reassignRef.trim() };
    }
    if (action === 'split') {
      const rows = splitRows.filter((r) => r.amount && r.referenceId.trim());
      if (!rows.length) { toast.error('Add at least one split row with amount + reference ID'); return; }
      body.splitAllocations = rows.map((r) => ({
        amount: Number(r.amount), allocatedTo: { type: r.type, referenceId: r.referenceId.trim() },
      }));
    }
    setSaving(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/reconciliation/exceptions/${encodeURIComponent(exception.exceptionKey)}/action`,
        body,
      );
      toast.success(`'${meta.label}' applied`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to apply '${meta.label}'`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="exception-action-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{exception.summary}</DialogDescription>
        </DialogHeader>

        {action === 'edit' && (
          <div className="space-y-3">
            <div>
              <Label>Field name</Label>
              <Input data-testid="edit-field-input" value={editField} onChange={(e) => setEditField(e.target.value)} placeholder="e.g. amount" />
            </div>
            <div>
              <Label>New value</Label>
              <Input data-testid="edit-value-input" value={editValue} onChange={(e) => setEditValue(e.target.value)} />
            </div>
          </div>
        )}

        {action === 'reassign' && (
          <div className="space-y-3">
            <div>
              <Label>Target type</Label>
              <Select value={reassignType} onValueChange={setReassignType}>
                <SelectTrigger data-testid="reassign-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TARGET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference ID</Label>
              <Input data-testid="reassign-ref-input" value={reassignRef} onChange={(e) => setReassignRef(e.target.value)} placeholder="e.g. UC1094" />
            </div>
          </div>
        )}

        {action === 'split' && (
          <div className="space-y-3">
            {splitRows.map((row, i) => (
              <div key={i} className="grid grid-cols-3 gap-2" data-testid={`split-row-${i}`}>
                <Input placeholder="Amount" type="number" value={row.amount} onChange={(e) => updateSplitRow(i, { amount: e.target.value })} data-testid={`split-amount-${i}`} />
                <Select value={row.type} onValueChange={(v) => updateSplitRow(i, { type: v })}>
                  <SelectTrigger data-testid={`split-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{TARGET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Reference ID" value={row.referenceId} onChange={(e) => updateSplitRow(i, { referenceId: e.target.value })} data-testid={`split-ref-${i}`} />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" data-testid="add-split-row-btn"
              onClick={() => setSplitRows((rows) => [...rows, { amount: '', type: 'ucc', referenceId: '' }])}>
              + Add split row
            </Button>
          </div>
        )}

        <div>
          <Label>Reason (optional)</Label>
          <Textarea data-testid="action-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="action-dialog-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="action-dialog-submit-btn" className="bg-teal-600 hover:bg-teal-700 text-white">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ExceptionRow = ({ exc, onActed }) => {
  const [dialogAction, setDialogAction] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [auditLog, setAuditLog] = useState(null);

  const toggleAudit = () => {
    if (showAudit) { setShowAudit(false); return; }
    setShowAudit(true);
    if (!auditLog) {
      axios.get(`${BACKEND_URL}/api/historical-records/audit-log`, { params: { historicalId: exc.refId } })
        .then((r) => setAuditLog(r.data || []))
        .catch(() => setAuditLog([]));
    }
  };

  const actions = availableActions(exc);

  return (
    <Card className="p-4 border border-amber-100 bg-amber-50/30" data-testid={`exception-row-${exc.exceptionKey}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge className="bg-amber-100 text-amber-800 text-[10px] uppercase">{EXCEPTION_LABELS[exc.type] || exc.type}</Badge>
            <span className="text-[11px] text-slate-400">{exc.refType} · {exc.refId}</span>
          </div>
          <p className="text-sm text-slate-700" data-testid={`exception-summary-${exc.exceptionKey}`}>{exc.summary}</p>
        </div>
        <Button variant="ghost" size="sm" onClick={toggleAudit} data-testid={`view-audit-${exc.exceptionKey}`}>
          <History className="h-3.5 w-3.5 mr-1" />Audit trail
        </Button>
      </div>

      {showAudit && (
        <div className="mt-3 bg-slate-900 text-slate-100 rounded-lg p-3 text-[11px] font-mono max-h-40 overflow-y-auto"
          data-testid={`audit-log-${exc.exceptionKey}`}>
          {!auditLog ? (
            <div className="text-slate-400">Loading…</div>
          ) : auditLog.length === 0 ? (
            <div className="text-slate-400">No audit entries yet.</div>
          ) : auditLog.map((e, i) => (
            <div key={i} className="mb-1">[{e.occurredAt}] {e.attemptedAction} by {e.attemptedBy} — {e.reason}</div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3 flex-wrap">
        {actions.map((a) => {
          const meta = ACTION_META[a];
          const Icon = meta.icon;
          return (
            <Button key={a} size="sm" variant="outline" className={meta.cls}
              onClick={() => setDialogAction(a)} data-testid={`action-${a}-${exc.exceptionKey}`}>
              <Icon className="h-3.5 w-3.5 mr-1" />{meta.label}
            </Button>
          );
        })}
      </div>

      {dialogAction && (
        <ActionDialog
          exception={exc}
          action={dialogAction}
          onClose={() => setDialogAction(null)}
          onDone={() => { setDialogAction(null); onActed(); }}
        />
      )}
    </Card>
  );
};

const AccountingExceptions = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');

  const load = () => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/reconciliation/exceptions`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load exceptions'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-16" data-testid="exceptions-loading">
        <Loader2 className="h-7 w-7 text-teal-600 animate-spin" />
      </div>
    );
  }

  const exceptions = data?.exceptions || [];
  const byType = data?.byType || {};
  const visible = typeFilter === 'all' ? exceptions : exceptions.filter((e) => e.type === typeFilter);

  return (
    <div className="space-y-4" data-testid="accounting-exceptions-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" />Reconciliation Exceptions
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Open exceptions across the reconstruction pipeline — surfaced, never auto-resolved. Take one of the actions below to resolve.
          </p>
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-56 h-8 text-xs" data-testid="exception-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types ({data?.total || 0})</SelectItem>
            {Object.entries(byType).map(([t, c]) => (
              <SelectItem key={t} value={t}>{EXCEPTION_LABELS[t] || t} ({c})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {visible.length === 0 ? (
        <Card className="p-12 text-center" data-testid="exceptions-empty-state">
          <CheckCircle2 className="h-10 w-10 text-emerald-300 mx-auto mb-3" />
          <p className="text-slate-500">No open exceptions — reconstructed data is fully reconciled right now.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="exceptions-list">
          {visible.map((exc) => (
            <ExceptionRow key={exc.exceptionKey} exc={exc} onActed={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default AccountingExceptions;
